// Тело полноэкранного предпросмотра: телефон с текущим экраном урока и
// управление прохождением. Смонтирован под `key` урока — смена урока начинает
// прохождение заново, без ручного сброса.
import type { ReactNode } from 'react'
import { LiveQuizMascot } from '@/features/editor/components/blocks/previews/quizMascotContext'
import type { Block } from '@/types/blocks'
import type { ContentLang } from '@/features/editor/editorSlice'
import { LessonProgressContext } from '../blocks/previews/lessonProgress'
import { PhoneCanvas } from '../blocks/previews/PhoneCanvas'
import { renderBlockPreview } from '../blocks/previews/renderBlockPreview'
import { PreviewFinish } from './PreviewFinish'
import { PreviewLoading } from './PreviewLoading'
import { PreviewRunHeader } from './PreviewRunHeader'
import { buildPreviewRun } from './previewRun'
import { useLessonRun } from './useLessonRun'
import { useMessages } from '@/i18n'
import { messages } from './messages'

interface Props {
  blocks: Block[]
  contentLang: ContentLang
  /** Урок, который проходим: его смена начинает прохождение заново. Приходит
   *  ЗНАЧЕНИЕМ, а не React-`key`, — иначе вместе с деревом размонтировалась бы
   *  шапка, в которой живёт сам селектор урока. */
  lessonId: number | null
  onClose: () => void
  /** Навигация шапки (урок + язык) — рисует владелец диалога. */
  controls?: ReactNode
}

export const LessonRunView = ({ blocks, contentLang, lessonId, onClose, controls }: Props) => {
  const m = useMessages(messages)
  const run = useLessonRun(blocks, contentLang, lessonId)

  return (
    <div className="flex h-full min-h-0 flex-col bg-surface">
      <PreviewRunHeader
        position={run.block ? m.position(run.index + 1, run.total) : null}
        canGoBack={run.canGoBack}
        onBack={run.back}
        onRestart={run.reset}
        onClose={onClose}
        controls={controls}
      />
      <div className="flex min-h-0 flex-1 items-center justify-center p-4">
        <div className="aspect-[390/760] h-full max-w-full">
          <PhoneCanvas className="rounded-2xl shadow-sm">
            {run.block ? (
              <LiveQuizMascot>
                <LessonProgressContext.Provider
                  value={{
                    total: run.total,
                    current: run.index,
                    onBack: run.canGoBack ? run.back : undefined,
                  }}
                >
                  {/* Правка дизайн-блока сюда не передаётся: в прохождении клик по
                    элементу иначе одновременно «отвечает» и редактирует. */}
                  {renderBlockPreview(run.block, contentLang, {
                    run: buildPreviewRun(run, contentLang),
                  })}
                </LessonProgressContext.Provider>
              </LiveQuizMascot>
            ) : run.total > 0 ? (
              <PreviewFinish onRestart={run.reset} />
            ) : (
              // Блоки урока ещё едут (кадр смены урока): объявлять пустой
              // список пройденным уроком — врать про чужой урок.
              <PreviewLoading />
            )}
          </PhoneCanvas>
        </div>
      </div>
    </div>
  )
}
